import Button from '2-core-elements/Buttons/Button';
import RadioSelect from '5-react-form-apps/FormComponents/RadioSelect/RadioSelect';
import FormFieldWrapper from '6-my-mcc/FormElements/FormFieldWrapper/FormFieldWrapper';
import { useEffect, type FC } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import type { CommunicationPreferencesFormType } from '../../Utils/useCommunicationPreferencesConfig';
import useClubEmailPreferencesMutation, { type ClubEmailPreferencesRequestBody } from '../../Utils/useClubEmailPreferencesMutation';
import LoadingIndicator from '3-components/LoadingIndicator/LoadingIndicator';
import useCommunicationPreferencesValue from '../../Utils/useCommunicationPreferencesValue';
import { pushGTMEvent } from 'utils/eventGTM';

type FormData = {
    [K in CommunicationPreferencesFormType['code']]: string;
}

type FormClubEmailPreferencesProps = {
    emailPreferencesConfig: {
        config: CommunicationPreferencesFormType;
        value: string | null;
    }[];
    onSubmitSuccess: () => void;
}

const FormClubEmailPreferences: FC<FormClubEmailPreferencesProps> = ({ emailPreferencesConfig, onSubmitSuccess }) => {
    const methods = useForm<FormData>();
    const { control, handleSubmit } = methods;
    const submitFormMutation = useClubEmailPreferencesMutation();
    const { refetch: preferencesValueRefetch, isFetching: isPreferencesValueFetching } = useCommunicationPreferencesValue();

    useEffect(() => {
        pushGTMEvent({
            actionType: 'form_start',
            name: 'Club email preferences',
            type: 'Communication Preferences',
        });
    }, []);

    const onSubmit = (data: FormData): void => {
        const preferences = emailPreferencesConfig.reduce<Record<string, boolean>>((acc, item) => {
            const key = item.config.code.charAt(0).toLowerCase() + item.config.code.slice(1);
            acc[key] = data[item.config.code]?.toLocaleLowerCase?.() === 'yes';
            return acc;
        }, {}) as ClubEmailPreferencesRequestBody;

        submitFormMutation.mutate(preferences, {
            onSuccess: async() => {
                await preferencesValueRefetch();
                onSubmitSuccess();
            },
        });
    };

    return (
        <div className="mcc-form-wrapper">
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="form-club-email-preferences">
                        {emailPreferencesConfig.map((item): JSX.Element => {
                            return (
                                <FormFieldWrapper
                                    key={`form-club-email-preferences-${item.config.code}`}
                                    id={`form-club-email-preferences-${item.config.code}`}
                                    label={item.config.name}
                                    description={item.config.description}
                                    frequency={item.config.frequency}
                                >
                                    <Controller
                                        name={item.config.code}
                                        control={control}
                                        defaultValue={typeof item.value === 'string' ? item.value : 'No'}
                                        render={({ field }): JSX.Element => {
                                            return (
                                                <RadioSelect
                                                    value={field.value || ''}
                                                    onChange={(value): void => {
                                                        field.onChange(value);
                                                    }}
                                                >
                                                    <RadioSelect.Item value="Yes" label="Yes" />
                                                    <RadioSelect.Item value="No" label="No" />
                                                </RadioSelect>
                                            );
                                        }}
                                    />
                                </FormFieldWrapper>
                            );
                        })}
                    </div>

                    {submitFormMutation.isLoading || isPreferencesValueFetching ? (
                        <LoadingIndicator />
                    ) : (
                        <Button type="submit" fullWidth={true}>Save</Button>
                    )}
                </form>
            </FormProvider>
        </div>
    );
};

export default FormClubEmailPreferences;
